import * as React from 'react'
import { CalendarIcon } from 'lucide-react'
import type { Matcher } from 'react-day-picker'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { cn, toDisplayDate, toDateInputValue, formatDate, parseDateInput } from '@/lib/utils'

export interface DatePickerProps extends React.AriaAttributes {
  /** Stored value — 'YYYY-MM-DD' (or any value toDisplayDate understands). */
  value?: string | null
  /** Emits 'YYYY-MM-DD' (LOCAL, no UTC shift) or null when cleared. */
  onChange: (value: string | null) => void
  id?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  className?: string
  /** Optional bounds as 'YYYY-MM-DD'. */
  min?: string
  max?: string
}

/**
 * Type-or-pick date field. Users can type/paste a date (common formats), or use
 * the calendar popover (with month + year dropdowns). Emits the same
 * 'YYYY-MM-DD' string the native <input type="date"> it replaces produced, via
 * the timezone-safe helpers — so backends are unaffected.
 */
export function DatePicker({
  value,
  onChange,
  id,
  placeholder = 'Select or type a date',
  disabled,
  required,
  className,
  min,
  max,
  ...aria
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false)
  const [text, setText] = React.useState<string>(() => (value ? formatDate(value) : ''))
  const [invalid, setInvalid] = React.useState(false)
  const focusedRef = React.useRef(false)

  // Keep the visible text in sync with the external value when not being edited.
  React.useEffect(() => {
    if (!focusedRef.current) setText(value ? formatDate(value) : '')
  }, [value])

  const selected = toDisplayDate(value) ?? undefined

  const commit = (raw: string) => {
    const t = raw.trim()
    if (!t) {
      setInvalid(false)
      onChange(null)
      return
    }
    const parsed = parseDateInput(t)
    if (parsed) {
      setInvalid(false)
      setText(formatDate(parsed))
      onChange(toDateInputValue(parsed))
    } else {
      setInvalid(true)
    }
  }

  const handleCalendarSelect = (d?: Date) => {
    if (d) {
      onChange(toDateInputValue(d))
      setText(formatDate(d))
    } else {
      onChange(null)
      setText('')
    }
    setInvalid(false)
    setOpen(false)
  }

  const minDate = min ? (toDisplayDate(min) ?? undefined) : undefined
  const maxDate = max ? (toDisplayDate(max) ?? undefined) : undefined
  const disabledMatcher: Matcher[] = []
  if (minDate) disabledMatcher.push({ before: minDate })
  if (maxDate) disabledMatcher.push({ after: maxDate })
  const thisYear = new Date().getFullYear()

  return (
    <div className={cn('relative', className)}>
      <Input
        id={id}
        type="text"
        autoComplete="off"
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        {...aria}
        aria-invalid={invalid || aria['aria-invalid'] || undefined}
        className="pr-10"
        onFocus={() => {
          focusedRef.current = true
        }}
        onChange={(e) => {
          setText(e.target.value)
          setInvalid(false)
        }}
        onBlur={(e) => {
          focusedRef.current = false
          commit(e.target.value)
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit((e.target as HTMLInputElement).value)
          }
        }}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            aria-label="Open calendar"
            className="absolute right-0 top-0 h-10 w-10 text-muted-foreground hover:text-foreground"
          >
            <CalendarIcon className="h-4 w-4" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            defaultMonth={selected}
            onSelect={handleCalendarSelect}
            captionLayout="dropdown-buttons"
            fromYear={1950}
            toYear={thisYear + 10}
            disabled={disabledMatcher.length ? disabledMatcher : undefined}
            initialFocus
          />
        </PopoverContent>
      </Popover>
      {invalid && (
        <p className="mt-1 text-xs text-destructive" role="alert">
          Enter a valid date, e.g. {formatDate(new Date())}.
        </p>
      )}
    </div>
  )
}

export interface DateTimePickerProps {
  /** Stored value — 'YYYY-MM-DDTHH:MM' (browser-local, no timezone suffix). */
  value?: string | null
  onChange: (value: string | null) => void
  id?: string
  disabled?: boolean
  required?: boolean
  className?: string
  'aria-label'?: string
}

/**
 * Date + time field that preserves the native datetime-local contract
 * ('YYYY-MM-DDTHH:MM', browser-local). Reuses DatePicker for the date part
 * (type/paste + calendar) and a time input for the time.
 */
export function DateTimePicker({
  value,
  onChange,
  id,
  disabled,
  required,
  className,
  'aria-label': ariaLabel,
}: DateTimePickerProps) {
  const [datePart, timePart] = React.useMemo<[string, string]>(() => {
    if (!value) return ['', '']
    const [d, t] = String(value).split('T')
    return [d || '', (t || '').slice(0, 5)]
  }, [value])

  const emit = (d: string | null, t: string) => {
    if (!d) {
      onChange(null)
      return
    }
    onChange(`${d}T${t || '00:00'}`)
  }

  return (
    <div className={cn('flex gap-2', className)}>
      <DatePicker
        id={id}
        value={datePart || null}
        onChange={(d) => emit(d, timePart)}
        disabled={disabled}
        required={required}
        aria-label={ariaLabel}
        className="flex-1"
      />
      <Input
        type="time"
        value={timePart}
        disabled={disabled}
        aria-label={ariaLabel ? `${ariaLabel} time` : 'Time'}
        className="w-32"
        onChange={(e) => emit(datePart || toDateInputValue(new Date()), e.target.value)}
      />
    </div>
  )
}
